"use client";

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { QuoteRequestForm } from "../shared/QuoteRequestForm";
import { ImageWithFallback } from "../figma/ImageWithFallback";
import HeroImage from "@/components/ui/hero-image";
import { 
  ArrowLeft, 
  Phone, 
  CheckCircle, 
  MapPin,
  AlertTriangle,
} from "lucide-react";
import { CallToActionSection } from "../shared/CallToActionSection";
import { PhoneLink } from "@/utils/internal/linkHelpers";
import { DERATIZZAZIONE_PAGE } from "@/utils/constants/pages/deratizzazione";
import { CONTACT_INFO } from "@/utils/constants/app";
import { getPestImage, getPestImageClasses } from "@/utils/constants/images";

import { useRouter } from "next/navigation";
import { OTHER_SERVICES_PAGE } from "@/utils/constants/pages/other-services";

interface DeratizzationPageProps {
  onBackToHome?: () => void;
  heroImageUrl?: string | null;
}

export function DeratizzationPage({ onBackToHome, heroImageUrl }: Readonly<DeratizzationPageProps>) {
  const router = useRouter();
  return (
    <div className="min-h-screen bg-white">
      {/* Header with Back Button */}
      <div className="bg-green-600 text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center mb-4">
             <Button
              variant="ghost"
        onClick={() => (onBackToHome ? onBackToHome() : router.push("/"))}
              className="text-white hover:bg-green-700 mr-4"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              {OTHER_SERVICES_PAGE.HEADER.BACK_BUTTON}
            </Button>
          </div>
          <div className="text-center">
            <div className="text-center mb-4">
              <h1 className="text-4xl lg:text-5xl font-bold mb-2">
                {DERATIZZAZIONE_PAGE.HEADER.TITLE}
              </h1>
              <p className="text-xl opacity-90">
                {DERATIZZAZIONE_PAGE.HEADER.SUBTITLE}
              </p>
            </div>
            <p className="text-lg opacity-80 max-w-3xl mx-auto">
              {DERATIZZAZIONE_PAGE.HEADER.DESCRIPTION}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Service Overview */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <h2 className="text-3xl lg:text-4xl text-gray-900 mb-8">
                {DERATIZZAZIONE_PAGE.OVERVIEW.TITLE}
              </h2>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                {DERATIZZAZIONE_PAGE.OVERVIEW.DESCRIPTION}
              </p>

              <div className="space-y-6 mb-8">
                {DERATIZZAZIONE_PAGE.OVERVIEW.FEATURES.map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-green-600 mr-4 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">
                        {feature.title}
                      </h4>
                      <p className="text-gray-600">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 max-w-3xl">
                <QuoteRequestForm 
                  showPopupButton={true}
                  popupButtonText={DERATIZZAZIONE_PAGE.OVERVIEW.BUTTONS.PRIMARY}
                  popupButtonClassName="cta-button-enhanced bg-green-600 hover:bg-green-700 text-white px-8 py-4 text-lg font-semibold rounded-lg flex-1 sm:flex-auto sm:min-w-[220px]"
                  title={DERATIZZAZIONE_PAGE.OVERVIEW.FORM.TITLE}
                  subtitle={DERATIZZAZIONE_PAGE.OVERVIEW.FORM.SUBTITLE}
                  defaultService="deratizzazione"
                />
                <PhoneLink
                  phoneNumber={CONTACT_INFO.MAIN_PHONE}
                  className="inline-flex items-center justify-center px-6 py-3 text-base font-medium border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white rounded-lg transition-colors duration-300 flex-shrink-0 sm:min-w-[180px]"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Chiama Ora
                </PhoneLink>
              </div>
            </div>

            <div className="space-y-6">
              <div className="relative w-full h-80 lg:h-96 rounded-2xl shadow-2xl overflow-hidden">
                <HeroImage
                  src={heroImageUrl ?? DERATIZZAZIONE_PAGE.MAIN_IMAGE.URL}
                  alt={DERATIZZAZIONE_PAGE.MAIN_IMAGE.ALT}
                  sizes="(max-width:1024px) 100vw, 50vw"
                  priority
                />
              </div>
            </div>
          </div>

          {/* Techniques Section */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl text-gray-900 mb-6">
                {DERATIZZAZIONE_PAGE.METHODS.TITLE}
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                {DERATIZZAZIONE_PAGE.METHODS.SUBTITLE}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {DERATIZZAZIONE_PAGE.METHODS.TECHNIQUES.map((technique) => {
                const IconComponent = technique.icon;
                return (
                  <Card key={technique.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardHeader className="text-center pb-4">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <IconComponent className="w-8 h-8 text-green-600" />
                      </div>
                      <CardTitle className="text-xl">{technique.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 mb-6">{technique.description}</p>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3">
                            Caratteristiche
                          </h4>
                          <div className="space-y-2">
                            {technique.features.map((feature) => (
                              <div key={feature} className="flex items-center text-sm">
                                <CheckCircle className="w-4 h-4 text-green-600 mr-3 flex-shrink-0" />
                                <span className="text-gray-700">{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3">
                            Applicazioni
                          </h4>
                          <div className="flex flex-wrap gap-2">
              {technique.applications.map((app) => (
                              <span
                key={app}
                                className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm"
                              >
                                {app}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Rodent Types Section */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl text-gray-900 mb-6">
                {DERATIZZAZIONE_PAGE.RODENT_TYPES.TITLE}
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                {DERATIZZAZIONE_PAGE.RODENT_TYPES.SUBTITLE}
              </p>
            </div>

            <div className="space-y-8">
              {DERATIZZAZIONE_PAGE.RODENT_TYPES.SPECIES.map((rodent) => (
                <Card key={rodent.name} className="overflow-hidden border-0 shadow-lg">
                  <div className="grid lg:grid-cols-3 gap-0">
                    <div className="relative h-64 lg:h-auto">
                      <ImageWithFallback
                        src={getPestImage(rodent.name) || rodent.image}
                        alt={rodent.name}
                        className={`w-full h-full ${getPestImageClasses(rodent.name)} ${getPestImageClasses(rodent.name) === "object-contain" ? "bg-white" : ""}`}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    </div>
                    
                    <div className="lg:col-span-2 p-8">
                      <h3 className="text-2xl font-semibold text-gray-900 mb-4">{rodent.name}</h3>
                      <p className="text-lg text-gray-700 mb-6">{rodent.description}</p>
                      
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3">Caratteristiche Fisiche</h4>
                          <div className="space-y-2">
                            {rodent.characteristics.map((char) => (
                              <div key={char} className="flex items-center text-sm">
                                <div className="w-2 h-2 bg-blue-600 rounded-full mr-2 flex-shrink-0" />
                                <span className="text-gray-700">{char}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div className="space-y-4">
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                              <MapPin className="w-4 h-4 text-purple-600 mr-2" />
                              Habitat Preferito
                            </h4>
                            <p className="text-gray-600 text-sm">{rodent.habitat}</p>
                          </div>
                          
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                              <AlertTriangle className="w-4 h-4 text-red-600 mr-2" />
                              Rischi Associati
                            </h4>
                            <p className="text-gray-600 text-sm">{rodent.risks}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Process Timeline */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl text-gray-900 mb-6">
                {DERATIZZAZIONE_PAGE.PROCESS.TITLE}
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                {DERATIZZAZIONE_PAGE.PROCESS.SUBTITLE}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-8">
        {DERATIZZAZIONE_PAGE.PROCESS.PHASES.map((phase) => {
                const IconComponent = phase.icon;
                return (
          <Card key={phase.step + phase.title} className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow relative">
                    <CardContent className="p-8">
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${phase.color}`}>
                        <IconComponent className="w-8 h-8" />
                      </div>
                      <div className="absolute -top-3 -left-3 w-8 h-8 bg-gray-900 text-white rounded-full flex items-center justify-center font-bold">
                        {phase.step}
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">{phase.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{phase.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Call to Action Section */}
          <CallToActionSection
            title={DERATIZZAZIONE_PAGE.CTA_SECTION.TITLE}
            description={DERATIZZAZIONE_PAGE.CTA_SECTION.DESCRIPTION}
            primaryButtonText={DERATIZZAZIONE_PAGE.CTA_SECTION.PRIMARY_BUTTON}
            formTitle={DERATIZZAZIONE_PAGE.CTA_SECTION.FORM_TITLE}
            formSubtitle={DERATIZZAZIONE_PAGE.CTA_SECTION.FORM_SUBTITLE}
            defaultService={DERATIZZAZIONE_PAGE.CTA_SECTION.DEFAULT_SERVICE}
            phoneNumber={CONTACT_INFO.MAIN_PHONE}
          />
        </div>
      </section>
    </div>
  );
}